import { useState } from 'react';
import { Button } from '@src/design-system/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@src/design-system/dialog';
import TiclickTable from '@src/design-system/molecules/ticlick-table';
import useTiclickTableConfig from '@shared/hooks/use-ticlick-table-config';
import { TABLES_KEYS } from '@shared/constants/tables-keys';
import reminderLogsApi from '@src/features/reminders/services/reminder-logs-api';
import { Eye, RefreshLeftSquare } from 'iconsax-react';
import type { TReminder } from '@src/shared/types/reminder';
import type { Row } from '@tanstack/react-table';
import { ReminderLogResendButton } from '@src/features/reminders/components/all-reminders/automated-reminders/reminder-log-resend-button';
import { bulkReminderLogsColumns } from './bulk-reminder-logs-columns';
import { AutomatedReminderLogViewButton } from '../automated-reminders/automated-log-view-button';
import { ActionItemProps } from '@src/design-system/molecules/ticlick-table/ticlick-table-table-actions';
import { TReminderLog } from '@src/shared/types/reminder';

function useBulkReminderLogsTable(reminderId: number, enabled: boolean) {
  const actionsTable: ActionItemProps<TReminderLog>[] = [
    {
      key: 'resend',
      visibility: 'selection',
      render: (table) => (
        <ReminderLogResendButton
          ids={table.getSelectedRowModel().rows.map((row) => row.original.id)}
          trigger={
            <Button
              type='button'
              variant='ghost'
              className='text-[#211835] border border-[#DBD3F0] h-[40px] px-2.5'
            >
              <RefreshLeftSquare className='size-4 text-secondary-1000' />
              Resend
            </Button>
          }
        />
      ),
    },
  ];

  const table = useTiclickTableConfig({
    fetchingData: {
      fetchFn: reminderLogsApi.useFetch,
      query: { reminder: reminderId },
      options: { enabled: enabled && Boolean(reminderId) },
    },
    options: {
      columns: bulkReminderLogsColumns,
    },
    identifyingData: { tableKey: TABLES_KEYS.bulkReminderLogsDialogTable },
    rowActions: [
      {
        key: 'retry',
        render: (row) => <ReminderLogResendButton ids={[row.original.id]} />,
        minWidth: 36,
      },
      {
        key: 'view',
        render: (row) => <AutomatedReminderLogViewButton row={row} />,
        minWidth: 36,
      },
    ],
  });

  return { table, actionsTable };
}

export function BulkReminderLogsDialogButton({ row }: { row: Row<TReminder> }) {
  const [open, setOpen] = useState(false);
  const reminderId = row.original.id;
  const { table, actionsTable } = useBulkReminderLogsTable(reminderId, open);

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
    >
      <DialogTrigger asChild>
        <Button
          type='button'
          variant='ghost'
          size='iconSm'
          aria-label='View reminder logs'
        >
          <Eye
            size={18}
            variant='TwoTone'
          />
        </Button>
      </DialogTrigger>
      <DialogContent
        className='max-w-[90%] flex flex-col overflow-hidden p-0 gap-0 sm:rounded-xl'
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className='rounded-none border-b border-primary-100 bg-white shrink-0'>
          <DialogTitle className='text-xl font-semibold text-gray-1000'>
            Details
          </DialogTitle>
        </DialogHeader>
        <DialogBody className='flex flex-1 flex-col min-h-0 overflow-auto p-4'>
          <TiclickTable
            table={table}
            props={{ searchInputProps: { queryKey: 'name' } }}
            actions={actionsTable}
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
